/**
 * WebSocket message models for ElevenLabs Conversational AI API
 */

import { CLIENT_MESSAGE_TYPES, LANGUAGE_CODES } from '../enums/websocketMessageTypes.js';

/**
 * Client to Server Messages
 */

export class ConversationInitiationMessage {
  constructor({ 
    conversationConfigOverride = {}, 
    customLLMExtraBody = {}, 
    dynamicVariables = {} 
  } = {}) {
    this.type = CLIENT_MESSAGE_TYPES.CONVERSATION_INITIATION;
    this.conversation_config_override = conversationConfigOverride;
    this.custom_llm_extra_body = customLLMExtraBody;
    this.dynamic_variables = dynamicVariables;
  }

  static create({
    agentPrompt,
    firstMessage,
    language = LANGUAGE_CODES.ENGLISH,
    voiceId,
    temperature = 0.7,
    maxTokens = 150,
    dynamicVariables = {}
  } = {}) {
    // Build agent configuration - only include fields that have values
    const agentConfig = {
      ...(language && { language }),
      ...(firstMessage && { first_message: firstMessage }),
      ...(agentPrompt && { prompt: { prompt: agentPrompt } })
    };

    // Build TTS configuration - only include if voice_id is provided
    const ttsConfig = voiceId ? { voice_id: voiceId } : undefined;

    // Build conversation config override - only include sections that have content
    const conversationConfigOverride = {};
    if (Object.keys(agentConfig).length > 0) {
      conversationConfigOverride.agent = agentConfig;
    }
    if (ttsConfig) {
      conversationConfigOverride.tts = ttsConfig;
    }

    return new ConversationInitiationMessage({
      // Only include conversation_config_override if it has content
      ...(Object.keys(conversationConfigOverride).length > 0 && {
        conversationConfigOverride
      }),
      customLLMExtraBody: {
        temperature,
        max_tokens: maxTokens
      },
      dynamicVariables: dynamicVariables || {}
    });
  }
}

export class UserAudioChunkMessage {
  constructor(audioData) {
    if (!(audioData instanceof ArrayBuffer)) {
      throw new Error('Audio data must be an ArrayBuffer');
    }
    
    // Convert ArrayBuffer to base64
    const uint8Array = new Uint8Array(audioData);
    const base64Audio = btoa(String.fromCharCode.apply(null, uint8Array));
    
    this.user_audio_chunk = base64Audio;
  }
}

export class PongMessage {
  constructor(eventId) {
    this.type = CLIENT_MESSAGE_TYPES.PONG;
    this.event_id = eventId;
  }
}

export class ClientToolResultMessage {
  constructor(toolCallId, result, isError = false) {
    this.type = CLIENT_MESSAGE_TYPES.CLIENT_TOOL_RESULT;
    this.tool_call_id = toolCallId;
    this.result = result;
    this.is_error = isError;
  }
}

export class ContextualUpdateMessage {
  constructor(text) {
    this.type = CLIENT_MESSAGE_TYPES.CONTEXTUAL_UPDATE;
    this.text = text;
  }
}

export class UserMessage {
  constructor(text) {
    this.type = CLIENT_MESSAGE_TYPES.USER_MESSAGE;
    this.text = text;
  }
}

export class UserActivityMessage {
  constructor() {
    this.type = CLIENT_MESSAGE_TYPES.USER_ACTIVITY;
  }
}

/**
 * Server to Client Message Parsers
 */

export class WebSocketMessageParser {
  static parse(rawMessage) {
    try {
      const message = JSON.parse(rawMessage);
      return message;
    } catch (error) {
      return null;
    }
  }

  static isConversationInitiationMetadata(message) {
    return message?.type === 'conversation_initiation_metadata' && 
           message?.conversation_initiation_metadata_event;
  }

  static isVADScore(message) {
    return message?.type === 'vad_score' && 
           message?.vad_score_event;
  }

  static isUserTranscript(message) {
    return message?.type === 'user_transcript' && 
           message?.user_transcription_event;
  }

  static isTentativeAgentResponse(message) {
    return message?.type === 'internal_tentative_agent_response' && 
           message?.tentative_agent_response_internal_event;
  }

  static isAgentResponse(message) {
    return message?.type === 'agent_response' && 
           message?.agent_response_event;
  }

  static isAudioResponse(message) {
    return message?.type === 'audio' && 
           message?.audio_event;
  }

  static isPing(message) {
    return message?.type === 'ping' && 
           message?.ping_event;
  }

  static isClientToolCall(message) {
    return message?.type === 'client_tool_call' && 
           message?.client_tool_call;
  }

  static isAgentResponseCorrection(message) {
    return message?.type === 'agent_response_correction' && 
           message?.agent_response_correction_event;
  }

  static isInterruption(message) {
    return message?.type === 'interruption' && 
           message?.interruption_event;
  }

  static extractAudioData(message) {
    if (!this.isAudioResponse(message)) {
      return null;
    }

    const audioBase64 = message.audio_event.audio_base_64;
    if (!audioBase64) {
      return null;
    }

    try {
      // Convert base64 to ArrayBuffer
      const audioData = Uint8Array.from(atob(audioBase64), c => c.charCodeAt(0));
      return audioData.buffer;
    } catch (error) {
      return null;
    }
  }

  static extractTranscript(message) {
    if (this.isUserTranscript(message)) {
      return message.user_transcription_event.user_transcript;
    }
    if (this.isAgentResponse(message)) {
      return message.agent_response_event.agent_response;
    }
    if (this.isTentativeAgentResponse(message)) {
      return message.tentative_agent_response_internal_event.tentative_agent_response;
    }
    return null;
  }

  static extractConversationMetadata(message) {
    if (!this.isConversationInitiationMetadata(message)) {
      return null;
    }
    return message.conversation_initiation_metadata_event;
  }

  static extractVADScore(message) {
    if (!this.isVADScore(message)) {
      return null;
    }
    return message.vad_score_event.vad_score;
  }

  static extractPingEventId(message) {
    if (!this.isPing(message)) {
      return null;
    }
    return message.ping_event.event_id;
  }

  static extractToolCall(message) {
    if (!this.isClientToolCall(message)) {
      return null;
    }
    return message.client_tool_call;
  }
}